/**
 * The renderer.
 *
 * Paginated by section, one section per screen, review at the end. It reads a `FormSchema` and knows
 * nothing else: no schema id is special-cased, no field id appears in this file.
 */

import { useCallback, useMemo, useState } from 'react';
import { FieldControl } from './FieldControl.tsx';
import { ReviewStep } from './ReviewStep.tsx';
import { useFormEngine } from '../useFormEngine.ts';
import type { PhotoCaptureProps, SerialCaptureProps } from './capture-contract.tsx';
import type { PrefillBundle } from '../state.ts';
import type { SubmissionPayload } from '../payload.ts';
import type { ContextValue, FormSchema, PhotoRef } from '../types.ts';

export interface FormRendererProps {
  readonly schema: FormSchema;
  readonly prefill?: PrefillBundle;
  readonly context?: Readonly<Record<string, ContextValue>>;
  /**
   * Hands captured bytes to the blob store and returns the reference. Wired to
   * `capturePhoto` from `submit-core.ts` in the app; injected here so this component has no
   * dependency on IndexedDB.
   */
  readonly storePhoto: (blob: Blob, at: number) => Promise<PhotoRef>;
  /** Queues the submission. Wired to `submitToOutbox`. */
  readonly submit: (payload: SubmissionPayload) => Promise<void>;
  readonly onSubmitted?: (payload: SubmissionPayload) => void;
  readonly now?: () => number;
  readonly PhotoCapture?: (props: PhotoCaptureProps) => JSX.Element;
  readonly SerialCapture?: (props: SerialCaptureProps) => JSX.Element;
}

export function FormRenderer(props: FormRendererProps): JSX.Element {
  const { schema, prefill, context, storePhoto, submit, onSubmitted, now = Date.now, PhotoCapture, SerialCapture } = props;

  const engine = useFormEngine({
    schema,
    ...(prefill !== undefined ? { prefill } : {}),
    ...(context !== undefined ? { context } : {}),
    now,
  });

  const [showProblems, setShowProblems] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | undefined>(undefined);

  /**
   * Record-sourced answers currently on the form, counted over the VISIBLE fields — the same set the
   * review screen will list — so the promise in the header ("you'll check them on the review screen")
   * can never claim more than the review step shows.
   */
  const prefilledCount = useMemo(
    () =>
      engine.steps
        .flatMap((candidate) => candidate.fields)
        .filter((field) => engine.state.entries[field.id]?.provenance === 'prefill').length,
    [engine.steps, engine.state],
  );

  const store = useCallback((blob: Blob) => storePhoto(blob, now()), [storePhoto, now]);

  const advance = useCallback(() => {
    if (!engine.canAdvance) {
      setShowProblems(true);
      return;
    }
    setShowProblems(false);
    engine.next();
  }, [engine]);

  const send = useCallback(async () => {
    setSubmitting(true);
    setFailure(undefined);
    try {
      const payload = engine.build();
      await submit(payload);
      onSubmitted?.(payload);
    } catch (error) {
      // The outbox is the only path out, so a failure here is a local storage failure, not a
      // network one. Saying "check your connection" would be a lie and would send the technician
      // looking in the wrong place.
      setFailure(error instanceof Error ? error.message : 'Could not save this submission on the device.');
    } finally {
      setSubmitting(false);
    }
  }, [engine, submit, onSubmitted]);

  if (engine.onReview) {
    return (
      <div className="fx-form">
        <ReviewStep
          schema={schema}
          payload={engine.build()}
          problems={engine.problems}
          onGoTo={engine.goTo}
          onConfirmAll={engine.confirmAll}
          onSubmit={send}
          submitting={submitting}
        />
        {failure !== undefined && (
          <p className="fx-errors" role="alert">
            {failure}
          </p>
        )}
        <button type="button" onClick={engine.back}>
          Back
        </button>
      </div>
    );
  }

  const step = engine.step;
  if (!step) {
    return (
      <div className="fx-form">
        <p>This form has no questions for this visit.</p>
      </div>
    );
  }

  return (
    <div className="fx-form">
      <header className="fx-progress">
        <p>
          {schema.title} — step {engine.stepIndex + 1} of {engine.steps.length}
        </p>
        {/*
          A REAL `<progress>`, AND WHAT ITS NUMBER MEANS.
          =============================================
          `stepIndex` sections are FINISHED; the technician is standing in the next one. So
          `value={stepIndex}` out of `max={steps.length}` — on the first screen the bar is honestly
          empty, and it never reads 100% here because the review step is a different branch.

          It is deliberately NOT a question-weighted bar. `steps` is the *visible* set and display
          logic resizes it as answers land (answer No and three sections vanish), so a
          question-weighted percentage would move backwards mid-form and a bar that goes backwards is
          a bar nobody trusts. Sections completed is a fact that only ever increases.

          `steps.length` can legitimately shrink under the technician too; `max` follows it, which is
          why the visible "step X of Y" line stays — a lone bar cannot say that Y changed.
        */}
        <progress
          className="fx-progress__bar"
          value={engine.stepIndex}
          max={engine.steps.length}
          aria-label={`${engine.stepIndex} of ${engine.steps.length} sections finished`}
        />
        {/*
          MID-FORM SECTION NAVIGATION. On a form with 93 asked questions the single most valuable thing
          after an interruption is getting back to where you were, and the only movement available was
          Back/Next one section at a time — up to ten taps to reach section eleven, each one re-rendering
          a screenful of questions.

          Collapsed by default so it costs one tap and no vertical space on the 360×640 phone this runs
          on. Each entry says whether that section still has unanswered required questions, which is the
          other half of resumption: "where was I" and "what is still missing" are the same question.

          Jumping does NOT bypass validation. `engine.canAdvance` gates the Next button only; the submit
          button is gated separately on `engine.problems` being empty across the whole form, and
          `ReviewStep` already offers exactly this jump. What it does bypass is being *shouted at* on the
          way past, which is why the counts below are on screen the whole time instead.
        */}
        {engine.steps.length > 1 && (
          <details className="fx-sections">
            <summary>Jump to a section ({engine.steps.length} in this form)</summary>
            <ul className="fx-sections__list">
              {engine.steps.map((candidate, index) => {
                const outstanding = engine.problems.filter(
                  (p) => p.sectionId === candidate.section.id,
                ).length;
                const here = index === engine.stepIndex;
                return (
                  <li key={candidate.section.id}>
                    <button
                      type="button"
                      onClick={() => {
                        setShowProblems(false);
                        engine.goTo(candidate.section.id);
                      }}
                      data-outstanding={outstanding > 0 ? 'yes' : 'no'}
                      {...(here ? { 'aria-current': 'step' as const } : {})}
                    >
                      <span className="fx-sections__n">{index + 1}</span>
                      <span className="fx-sections__title">{candidate.section.title}</span>
                      {/*
                        The count is announced as words, not as a bare number beside a title — "3" on
                        its own could as easily be three questions as three problems.
                      */}
                      <span className="fx-sections__todo">
                        {outstanding === 0
                          ? 'nothing outstanding'
                          : `${outstanding} still need${outstanding === 1 ? 's' : ''} an answer`}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </details>
        )}
        {schema.source.provenance !== 'sourced' && (
          <p className="fx-help">
            Parts of this form are reconstructed rather than taken from an existing form. Fields marked{' '}
            <em>inferred</em> are under review.
          </p>
        )}
        {/*
          THE ONE-SCREEN CONFIRMATION PROMISE (field feedback 2). Prefilled fields no longer carry a
          per-field confirm tap — the badge under each is display-only. This line is what replaces the
          twelve taps: it tells the technician up front that everything filled in from the record gets
          reviewed in ONE place at the end, so the badges can be walked past without guilt. Count is
          `prefill` provenance only: `auto` values are the device's, and an override is already the
          technician's own answer.
        */}
        {prefilledCount > 0 && (
          <p className="fx-help">
            {prefilledCount} answer{prefilledCount === 1 ? ' was' : 's were'} filled in from the record. You’ll
            check them in one place on the review screen — only change one now if it is wrong.
          </p>
        )}
      </header>

      <h2>{step.section.title}</h2>
      {step.section.subtitle !== undefined && <p className="fx-subtitle">{step.section.subtitle}</p>}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          advance();
        }}
      >
        {step.fields.map((field) => (
          <FieldControl
            key={field.id}
            field={field}
            entry={engine.state.entries[field.id]}
            reference={engine.state.reference[field.id]}
            problems={showProblems ? engine.problemsHere.filter((p) => p.fieldId === field.id) : []}
            onChange={(value) => engine.set(field.id, value)}
            onRevert={() => engine.clear(field.id)}
            storePhoto={store}
            onCaptured={(photo) => engine.addPhoto(field.id, photo)}
            onRemovePhoto={(blobId) => engine.removePhoto(field.id, blobId)}
            {...(PhotoCapture !== undefined ? { PhotoCapture } : {})}
            {...(SerialCapture !== undefined ? { SerialCapture } : {})}
          />
        ))}

        <nav className="fx-nav">
          {engine.stepIndex > 0 && (
            <button type="button" onClick={engine.back}>
              Back
            </button>
          )}
          <button type="submit">{engine.stepIndex + 1 === engine.steps.length ? 'Review' : 'Next'}</button>
        </nav>

        {showProblems && engine.problemsHere.length > 0 && (
          <p className="fx-errors" role="alert">
            {engine.problemsHere.length} thing(s) on this page still need an answer.
          </p>
        )}
      </form>
    </div>
  );
}
